import React, { useMemo, useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
} from "@mui/material";
import AuthContext from "../../contexts/AuthContext";

const NoShowForm = () => {
  const { eventId, email: emailParam } = useParams();
  const { appStyle, appConfig, orgId, signedIn, email: userEmail } = useContext(AuthContext);
  const [message, setMessage] = useState("");
  const [canSubmit, setCanSubmit] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMap, setErrorMap] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  const decodedEmail = useMemo(() => {
    try {
      return emailParam ? decodeURIComponent(emailParam) : "";
    } catch (e) {
      console.log("Failed to decode email param", e);
      return emailParam || "";
    }
  }, [emailParam]);

  const [email, setEmail] = useState(
    decodedEmail ? decodedEmail.trim().toLowerCase() : ""
  );

  useEffect(() => {
    const scriptId = "recaptcha-v3-script";

    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = `https://www.google.com/recaptcha/api.js?render=${process.env.REACT_APP_RECAPTCHA_SITE_KEY}`;
      script.async = true;
      document.body.appendChild(script);
    }

    return () => {
      // Remove reCAPTCHA badge injected into the DOM
      const badge = document.querySelector(".grecaptcha-badge");
      if (badge && badge.parentNode) {
        badge.parentNode.removeChild(badge);
      }

      // Remove reCAPTCHA script
      const script = document.getElementById(scriptId);
      if (script && script.parentNode) {
        script.parentNode.removeChild(script);
      }

      // Defensive cleanup
      if (window.grecaptcha) {
        delete window.grecaptcha;
      }
    };
  }, []);

  useEffect(() => {
    if (signedIn && userEmail) {
        setEmail(userEmail);
    }

  },[signedIn, userEmail])

  useEffect(() => {
    if (appConfig) {
      setErrorMap({
        1001: `This page did not appear to load correctly. Please refresh and try again`,
        1002: `We cannot find this event. Please email the organizer directly at ${appConfig?.contactUsEmail}`,
        1003: `We cannot find this event. Please email the organizer directly at ${appConfig?.contactUsEmail}`,
        1004: `Unfortunately, we cannot find you in the system. Are you sure your entered your email correctly? Please try again or email the organizer directly at ${appConfig?.contactUsEmail}`,
        1005: `It looks like you do not have a ticket for this event. Are you sure you entered your email correctly? It must be the same email with which you purchased the ticket. Please try again or email the organizer directly at ${appConfig?.contactUsEmail}`,
        2001: "Please enter same email with which you purchased your ticket.",
        3001: `An unknown error has occured. Please email your organizer directly at ${appConfig.contactUsEmail}`,
        4001: `Unable to submit form. Please try again or email your organizer directly at ${appConfig.contactUsEmail}`,
      });
    }
  }, [appConfig]);

  useEffect(() => {
    if (orgId && eventId && errorMap) {
      validate();
    }
  }, [eventId, emailParam, orgId, errorMap]);

  const validate = async () => {
    const body = {
      orgId,
      eventId,
      email,
    };

    try {
      const result = await axios.post(
        `${process.env.REACT_APP_TNFT_SERVER}/noshow/validate`,
        body
      );
      if (result.data.success) {
        setErrorCode(null);
        setCanSubmit(true);
      }
    } catch (e) {
      const errorType = e?.response?.data?.errorType;
      if (!errorType) {
        console.log(e);
        return;
      }
      setErrorCode(errorType);
      setCanSubmit(false);
      return;
    }
  };

  const executeRecaptcha = async () => {
    if (!window.grecaptcha) {
      console.log("reCAPTCHA not yet available");
      return null;
    }

    return await window.grecaptcha.execute(
      process.env.REACT_APP_RECAPTCHA_SITE_KEY,
      { action: "noshow_submit" }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const recaptchaToken = await executeRecaptcha();

    if (!recaptchaToken) {
      console.log("reCAPTCHA failed or not ready");
      return;
    }

    const body = {
      orgId,
      eventId,
      email,
      message,
      recaptchaToken,
    };

    try {
      const result = await axios.post(
        `${process.env.REACT_APP_TNFT_SERVER}/noshow/submitform`,
        body
      );

      if (result.data?.success) {
        setSubmitted(true);
      }
    } catch (e) {
      setErrorCode("4001");
    }
  };

  if (!eventId) {
    return (
      <Container maxWidth="sm" sx={{ mt: 6 }}>
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          <img src={appStyle?.logo} height="175px" alt="Logo" />
        </Box>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h6" gutterBottom>
            Hmmmmm...
          </Typography>
          <Typography sx={{ mt: 1 }}>
            Looks like this link is not working. If you are unable to attend an
            event, please email the organizer at{" "}
            <a href={`mailto:${appConfig?.contactUsEmail}`}>
              {appConfig?.contactUsEmail}
            </a>
            .
          </Typography>
        </Paper>
      </Container>
    );
  }
  return (
    <Container maxWidth="sm" sx={{ mt: 6 }}>
      <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
        <img src={appStyle?.logo} height="175px" alt="Logo" />
      </Box>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom>
          Not able to attend? Let us know
        </Typography>
        {errorCode && errorMap?.[errorCode] && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorMap[errorCode]}
          </Alert>
        )}

        {!submitted ? (
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              required
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value.trim().toLowerCase())}
              onBlur={validate}
              margin="normal"
            />

            <TextField
              fullWidth
              label="Optional message"
              multiline
              minRows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              margin="normal"
            />

            <Button
              type="submit"
              variant="contained"
              disabled={!canSubmit}
              fullWidth
              sx={{ mt: 3 }}
            >
              Submit
            </Button>
          </Box>
        ) : (
          <Typography sx={{ mt: 2 }}>
            Thanks — the organizer has been notified.
          </Typography>
        )}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 3, display: "block" }}
        >
          This site is protected by reCAPTCHA and the Google{" "}
          <a
            href="https://policies.google.com/privacy"
            target="_blank"
            rel="noreferrer"
          >
            Privacy Policy
          </a>{" "}
          and{" "}
          <a
            href="https://policies.google.com/terms"
            target="_blank"
            rel="noreferrer"
          >
            Terms of Service
          </a>{" "}
          apply.
        </Typography>
      </Paper>
    </Container>
  );
};

export default NoShowForm;
